import React, { FC, useState } from 'react'
import {
  Box,
  Button,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  Avatar,
  useDisclosure,
  Flex,
  IconButton,
  Checkbox,
  Square,
  useBoolean
} from '@chakra-ui/react'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'
import { useHistory } from 'react-router'
import { use100vh } from 'react-div-100vh'
import Datetime from 'react-datetime'
import { Loader, BaseHeading, ActionModal } from '.'
import { ROUTES } from '../contants'
import { sleep } from '../utils/sleep'
import { ControlledInput, Input } from './form'
import useBVNStore from '../state/BVN.store'
import 'react-datetime/css/react-datetime.css'
import useMotorStore from '../state/Motor.store'
import useGadgetStore from '../state/Gadget.store'
import { BVN_NUMBER, LAST_FOUR_DIGITS } from '../utils/validations'

const SCREENS = {
  ENTER: 'BVN Verification',
  FOUND: 'BVN Confirmation',
  CONFIRM: 'Confirm Identity'
}

type ScreenProps = {
  setScreen: any
}

const BVN = ({ isOpen, onClose, route, type = 'motor' }: any) => {
  const [currentScreen, setScreen] = useState(SCREENS.ENTER)
  const height = use100vh()

  const renderScreen = () => {
    switch (currentScreen) {
      case SCREENS.ENTER:
        return <EnterBVN setScreen={setScreen} />
      case SCREENS.FOUND:
        return <ConfirmDetails setScreen={setScreen} />
      case SCREENS.CONFIRM:
        return <ConfirmIdentity type={type} route={route} />
      default:
        return <EnterBVN setScreen={setScreen} />
    }
  }

  const previousState = () => {
    if (currentScreen === SCREENS.ENTER) {
      onClose()
      return
    }
    setScreen(currentScreen === SCREENS.FOUND ? SCREENS.ENTER : SCREENS.FOUND)
  }
  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        bg="#fcfcfc"
        h="-webkit-fill-available"
        maxW={{ md: '480px !important' }}
        mx="auto">
        <Flex
          pos="relative"
          flexDir="column"
          maxW={{ md: '480px' }}
          w="full"
          mx="auto"
          pb="2.4rem"
          px="2.4rem"
          pt="1.2rem"
          h={height || undefined}>
          <HStack mb="1rem" pb="2.4rem">
            <IconButton
              onClick={previousState}
              justifyContent="flex-start"
              color="transparent"
              bg="transparent"
              w="30px"
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              _focus={{ bg: 'transparent' }}
              aria-label="Search database"
              icon={
                <svg
                  width="15"
                  height="14"
                  viewBox="0 0 15 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3.4451 6.10003H14.3996V7.89997H3.4451L8.27256 12.7274L7 14L0 7L7 0L8.27256 1.27256L3.4451 6.10003Z"
                    fill="#2E279D"
                  />
                </svg>
              }
            />
            <Heading
              fontWeight="600"
              ml="-48px !important"
              flex="1"
              textAlign="center"
              fontSize="1.6rem"
              color="black">
              {currentScreen}
            </Heading>
          </HStack>
          {renderScreen()}
        </Flex>
      </DrawerContent>
    </Drawer>
  )
}

export { BVN }

const EnterBVN = ({ setScreen }: ScreenProps) => {
  const [setBVN, bvn, loading] = useBVNStore((state) => [state.setBVN, state.bvn, state.loading])
  const { isOpen, onOpen, onClose } = useDisclosure()

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
    control
  } = useForm({
    mode: 'onChange',
    defaultValues: { bvn }
  })
  const onSubmit = async (data: any) => {
    try {
      await setBVN(data.bvn)
      setScreen(SCREENS.FOUND)
    } catch (error) {
      onOpen()
      console.log('dd')
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <BaseHeading title="Please Enter Your BVN for Verification" />
      <Text fontSize="1.2rem" mb="2rem">
        Your BVN is needed to confirm your identity. Please input your BVN below.
      </Text>
      <Controller
        control={control}
        rules={BVN_NUMBER}
        name="bvn"
        render={({ field }) => (
          <ControlledInput
            isValid={watch('bvn') && !errors.bvn && true}
            isError={errors.bvn && errors.bvn.message}
            label="BVN"
            id="bvn"
            type="number"
            inputMode="numeric"
            value={field.value}
            onChange={(e) => {
              if (e.target.value.length > 11) {
                e.preventDefault()
                return
              }
              field.onChange(e)
            }}
          />
        )}
      />

      {/* <Input
        type="number"
        isValid={watch('bvn') && touchedFields.bvn && !errors.bvn && true}
        isError={touchedFields.bvn && errors.bvn && errors.bvn.message}
        label="BVN"
        id="bvn"
        inputMode="numeric"
        value={watch('bvn')}
        formHook={register('bvn', {
          required: 'BVN is required',
          maxLength: { value: 11, message: 'BVN must be 11 digits' },
          minLength: { value: 11, message: 'BVN must be 11 digits' }
        })}
      /> */}
      <Button isLoading={loading} type="submit" isFullWidth variant="blue">
        Submit
      </Button>

      <ActionModal
        action="Try Again"
        error
        message="BVN Not Found"
        isOpen={isOpen}
        onClose={onClose}
      />
    </Box>
  )
}

const ConfirmDetails = ({ setScreen }: ScreenProps) => {
  const [verifyDetails, loading, email, BVNdetails, phone_number] = useBVNStore((state) => [
    state.verifyDetails,
    state.loading,
    state.email,
    state.BVNdetails,
    state.phone_number
  ])

  const [error, setError] = useState({ dob: '', last_4: '' })

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isValid },
    watch,
    control
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email,
      dob: BVNdetails && moment(BVNdetails?.dob).format('YYYY-MM-DD'),
      last_4_digit: ''
    }
  })
  const onSubmit = async (data: any) => {
    try {
      const response = await verifyDetails(data)
      if (response) {
        setScreen(SCREENS.CONFIRM)
      }
    } catch (err: any) {
      if (err.includes('Date')) {
        setError({ dob: err, last_4: '' })
      } else if (err.includes('digits')) {
        setError({ dob: '', last_4: err })
      }
      console.log({ errors })
    }
  }

  const inputProps = {
    placeholder: 'Date of Birth',
    autoComplete: 'off',
    readOnly: true
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <Loader text="Verification in progress..." loading={loading} />
      <BaseHeading title="Confirm Your Details" />
      <Text fontSize="1.2rem" mb="2rem">
        We’ll need the data you provide to confirm the BVN you provided is yours.{' '}
      </Text>

      <Box mb="2.4rem">
        <Controller
          control={control}
          name="dob"
          rules={{ required: 'Date of Birth is required' }}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <Datetime
              onChange={(date) => {
                setError({ dob: '', last_4: '' })
                onChange(moment(date).format('YYYY-MM-DD'))
              }}
              value={value}
              closeOnSelect
              dateFormat="YYYY-MM-DD"
              timeFormat={false}
              inputProps={{
                ...inputProps,
                style: { borderColor: error.dob ? '#FD397A' : '#DDDDDD' }
              }}
            />
          )}
        />

        {errors.dob && (
          <Text mt="0.7rem" fontSize="1rem" align="center" color="brand.red" d="flex">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.00065 13.6666C3.31865 13.6666 0.333984 10.6819 0.333984 6.99992C0.333984 3.31792 3.31865 0.333252 7.00065 0.333252C10.6827 0.333252 13.6673 3.31792 13.6673 6.99992C13.6673 10.6819 10.6827 13.6666 7.00065 13.6666ZM7.00065 12.3333C8.41514 12.3333 9.77169 11.7713 10.7719 10.7712C11.7721 9.77096 12.334 8.41441 12.334 6.99992C12.334 5.58543 11.7721 4.22888 10.7719 3.22868C9.77169 2.22849 8.41514 1.66659 7.00065 1.66659C5.58616 1.66659 4.22961 2.22849 3.22942 3.22868C2.22922 4.22888 1.66732 5.58543 1.66732 6.99992C1.66732 8.41441 2.22922 9.77096 3.22942 10.7712C4.22961 11.7713 5.58616 12.3333 7.00065 12.3333ZM6.33399 3.66658H7.66732V4.99992H6.33399V3.66658ZM6.33399 6.33325H7.66732V10.3333H6.33399V6.33325Z"
                fill="#FD397A"
              />
            </svg>
            <Text ml="0.5rem"> {errors.dob && errors.dob.message}</Text>
          </Text>
        )}
      </Box>

      <Controller
        control={control}
        rules={LAST_FOUR_DIGITS}
        name="last_4_digit"
        render={({ field }) => (
          <ControlledInput
            isValid={watch('last_4_digit') && !errors.last_4_digit && true}
            isError={error.last_4 || (errors.last_4_digit && errors.last_4_digit.message)}
            label="Last 4 digits of Mobile Number"
            id="last_4_digit"
            inputMode="numeric"
            type="number"
            value={field.value}
            info={`Hint: ${phone_number}-XXXX`}
            onChange={(e) => {
              setError({ dob: '', last_4: '' })
              if (!e.target.value) {
                e.preventDefault()
                return
              }
              if (e.target.value.length > 4) {
                e.preventDefault()
                return
              }
              field.onChange(e)
            }}
          />
        )}
      />

      <Input
        isValid={watch('email') && touchedFields.email && !errors.email && true}
        isError={touchedFields.email && errors.email && errors.email.message}
        label="Email address"
        id="email"
        type="email"
        value={watch('email')}
        formHook={register('email', {
          required: 'Email address is required',
          maxLength: { value: 100, message: 'Maximum of 100 characters' },
          pattern: {
            value: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: 'Email is invalid'
          }
        })}
      />
      <Button type="submit" isDisabled={!isValid} isFullWidth variant="blue">
        Verify Details
      </Button>
    </Box>
  )
}

const ConfirmIdentity = ({ route, type }: { route: any; type: string }) => {
  const [loading, setLoading] = useState(false)
  const [BVNdetails, email] = useBVNStore((state) => [state.BVNdetails, state.email])
  const onboardBuyPolicy = useMotorStore((state) => state.onboardBuyPolicy)
  const onboardBuyGadgetPolicy = useGadgetStore((state) => state.onboardBuyPolicy)
  const [accepted, setAccepted] = useBoolean()

  const onSubmit = async () => {
    route()
    if (type === 'gadget') {
      await onboardBuyGadgetPolicy()
    } else {
      await onboardBuyPolicy()
    }
  }
  return (
    <>
      <Loader text="Verification in progress..." loading={loading} />
      <Box flex={1}>
        <VStack mb="3.2rem">
          <Avatar
            boxSize="120px"
            mx="auto"
            size="2xl"
            name={`${BVNdetails.last_name} ${BVNdetails.first_name}`}
            src={`data:image/png;base64, ${BVNdetails.base64_image}`}
          />
        </VStack>
        <VStack justify="flex-start" align="flex-start" spacing="1rem">
          <Detail
            image="profile"
            title="Name"
            body={`${BVNdetails.last_name} ${BVNdetails.first_name} ${BVNdetails.middle_name}`}
          />
          <Detail image="email" title="Email" body={email} />
          <HStack w="full" spacing="1rem">
            <Detail image="gender" title="Gender" body={BVNdetails.gender} />
            <Detail image="date" title="Date of Birth" body={BVNdetails.dob} />
          </HStack>
        </VStack>
      </Box>
      <Checkbox onChange={setAccepted.toggle} my="2.4rem" size="lg">
        I acknowledge that the information displayed above is accurate and up-to-date at this time
      </Checkbox>
      <Button disabled={!accepted} onClick={onSubmit} variant="blue">
        Confirm Details
      </Button>
    </>
  )
}

type Props = {
  image: string
  title: string
  body: string
}
const Detail: FC<Props> = ({ image, title, body }) => {
  return (
    <HStack
      w="100%"
      boxShadow="0px 4px 12px rgba(154, 160, 166, 0.04)"
      borderRadius="1.2rem"
      p="0.8rem"
      bg="white">
      <Square mr="1.2rem" size="4rem" bg="#F7F7F7">
        <Image src={`/icons/${image}.svg`} />
      </Square>
      <VStack align="flex-start">
        <Text fontSize="1rem">{title}</Text>
        <Text color="black" fontWeight="500" fontSize="1.2rem">
          {body}
        </Text>
      </VStack>
    </HStack>
  )
}
